import { useId } from 'react'
import { Form, useSearchParams, useSubmit } from 'react-router'
import { useTranslation } from '#app/utils/i18n.ts'
import { useDebounce, useIsPending } from '#app/utils/misc.tsx'
import { Icon } from './ui/icon.tsx'
import { Input } from './ui/input.tsx'
import { Label } from './ui/label.tsx'
import { StatusButton } from './ui/status-button.tsx'

export function SearchBar({
	status,
	autoFocus = false,
	autoSubmit = false,
}: {
	status: 'idle' | 'pending' | 'success' | 'error'
	autoFocus?: boolean
	autoSubmit?: boolean
}) {
	const id = useId()
	const t = useTranslation()
	const [searchParams] = useSearchParams()
	const submit = useSubmit()
	const isSubmitting = useIsPending({
		formMethod: 'GET',
		formAction: '/users',
	})

	const handleFormChange = useDebounce(async (form: HTMLFormElement) => {
		await submit(form)
	}, 400)

	return (
		// no `flex-wrap` on the form: the stacked state (icon button under the
		// input) is never a wanted layout — when the slot narrows the input
		// shrinks instead (`min-w-0` below) rather than pushing the button down
		<Form
			method="GET"
			action="/users"
			className="flex items-center justify-center gap-2"
			onChange={(e) => autoSubmit && handleFormChange(e.currentTarget)}
		>
			{/* min-w-0: a flex item's default `min-width: auto` refuses to shrink
			    below its content, which is what pushed the button out */}
			<div className="min-w-0 flex-1">
				<Label htmlFor={id} className="sr-only">
					{t('search.label')}
				</Label>
				<Input
					type="search"
					name="search"
					id={id}
					defaultValue={searchParams.get('search') ?? ''}
					placeholder={t('search.label')}
					className="w-full"
					autoFocus={autoFocus}
				/>
			</div>
			<div>
				<StatusButton
					type="submit"
					status={isSubmitting ? 'pending' : status}
					className="flex w-full items-center justify-center"
				>
					<Icon name="magnifying-glass" size="md" />
					<span className="sr-only">{t('search.label')}</span>
				</StatusButton>
			</div>
		</Form>
	)
}
